import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X, Upload, Image as ImageIcon } from "lucide-react";
import { api, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";

const input = "w-full border border-brand-border focus:border-brand-ink outline-none px-3 py-2.5 text-sm rounded-sm bg-white";

const Modal = ({ open, onClose, title, children }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-sm">
                <div className="sticky top-0 bg-white border-b border-brand-border px-6 py-4 flex items-center justify-between">
                    <h2 className="font-heading font-bold text-lg">{title}</h2>
                    <button onClick={onClose} className="w-9 h-9 hover:bg-brand-bg rounded-sm flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
};

const BannerForm = ({ initial, products, categories, onSubmit, onCancel }) => {
    const [form, setForm] = useState({ type: "primary", image: "", title: "", subtitle: "", button_text: "", link_type: "url", link_value: "", order: 0, is_active: true, ...initial });
    const [uploading, setUploading] = useState(false);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Imagem muito grande (máx 10MB).");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd);
            setForm({ ...form, image: `${BACKEND_URL}${data.url}` });
            toast.success("Imagem enviada");
        } catch (err) { toast.error(err.response?.data?.detail || "Falha no upload"); }
        setUploading(false);
        e.target.value = "";
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); if (!form.image) return toast.error("Imagem é obrigatória"); onSubmit({ ...form, order: parseInt(form.order) || 0 }); }} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Tipo</label>
                    <select data-testid="banner-type" className={input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                        <option value="primary">Banner principal (hero)</option>
                        <option value="secondary">Banner secundário</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Ordem</label>
                    <input data-testid="banner-order" type="number" className={input} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Imagem *</label>
                {form.image && (
                    <div className="mb-2 relative w-full aspect-video bg-brand-bg rounded-sm overflow-hidden border border-brand-border">
                        <img src={form.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain" />
                        <button type="button" onClick={() => setForm({ ...form, image: "" })} className="absolute top-2 right-2 w-7 h-7 bg-black/70 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                )}
                <div className="flex gap-2">
                    <label className="inline-flex items-center gap-2 px-4 py-2.5 border border-brand-border hover:border-brand-ink text-sm font-semibold rounded-sm cursor-pointer">
                        <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Upload"}
                        <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="banner-upload" />
                    </label>
                    <input className={input + " flex-1"} placeholder="Ou cole URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Título</label>
                <input data-testid="banner-title" className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Subtítulo</label>
                <input data-testid="banner-subtitle" className={input} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Texto do botão</label>
                    <input data-testid="banner-button" className={input} value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} placeholder="Ver produtos" />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Tipo de link</label>
                    <select className={input} value={form.link_type} onChange={(e) => setForm({ ...form, link_type: e.target.value, link_value: "" })}>
                        <option value="url">URL externa / rota</option>
                        <option value="product">Produto</option>
                        <option value="category">Categoria</option>
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Destino</label>
                {form.link_type === "product" ? (
                    <select className={input} value={form.link_value} onChange={(e) => setForm({ ...form, link_value: e.target.value })}>
                        <option value="">Selecione um produto</option>
                        {products.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
                    </select>
                ) : form.link_type === "category" ? (
                    <select className={input} value={form.link_value} onChange={(e) => setForm({ ...form, link_value: e.target.value })}>
                        <option value="">Selecione uma categoria</option>
                        {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                    </select>
                ) : (
                    <input className={input} value={form.link_value} onChange={(e) => setForm({ ...form, link_value: e.target.value })} placeholder="/produtos ou https://..." />
                )}
            </div>

            <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="banner-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Ativo
            </label>

            <div className="flex justify-end gap-2 pt-4 border-t border-brand-border">
                <button type="button" onClick={onCancel} className="px-5 py-2.5 text-sm font-semibold hover:bg-brand-bg rounded-sm">Cancelar</button>
                <button type="submit" data-testid="banner-save" className="px-6 py-2.5 bg-brand-ink text-white text-sm font-semibold rounded-sm hover:bg-brand-dark">Salvar</button>
            </div>
        </form>
    );
};

const Banners = () => {
    const [items, setItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = () => Promise.all([
        api.get("/admin/banners"),
        api.get("/admin/products"),
        api.get("/admin/categories"),
    ]).then(([b, p, c]) => { setItems(b.data); setProducts(p.data); setCategories(c.data); });

    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            if (editing) await api.put(`/admin/banners/${editing.id}`, data);
            else await api.post("/admin/banners", data);
            toast.success(editing ? "Atualizado" : "Criado");
            setOpen(false);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    };

    const remove = async (b) => {
        if (!window.confirm("Excluir banner?")) return;
        await api.delete(`/admin/banners/${b.id}`);
        toast.success("Excluído");
        load();
    };

    const toggle = async (b) => {
        await api.put(`/admin/banners/${b.id}`, { is_active: !b.is_active });
        load();
    };

    return (
        <div data-testid="admin-banners-page" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1">Vitrine</div>
                    <h1 className="font-heading font-bold text-3xl text-brand-ink">Banners</h1>
                    <p className="text-sm text-brand-inkSoft mt-1">{items.length} banner{items.length !== 1 ? "s" : ""} cadastrado{items.length !== 1 ? "s" : ""}. O banner principal aparece como Hero da home.</p>
                </div>
                <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="banner-create-btn" className="inline-flex items-center gap-2 bg-brand-ink text-white hover:bg-brand-dark px-5 py-3 text-sm font-semibold rounded-sm">
                    <Plus className="w-4 h-4" /> Novo banner
                </button>
            </div>

            <div className="space-y-4">
                {items.map((b) => (
                    <div key={b.id} className={`bg-white border border-brand-border rounded-sm overflow-hidden flex flex-col sm:flex-row ${!b.is_active ? "opacity-60" : ""}`} data-testid={`banner-${b.id}`}>
                        <div className="sm:w-64 aspect-video sm:aspect-auto sm:h-40 bg-brand-ink flex items-center justify-center overflow-hidden flex-shrink-0">
                            {b.image ? <img src={b.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain" /> : <ImageIcon className="w-8 h-8 text-brand-inkSoft" />}
                        </div>
                        <div className="flex-1 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-sm ${b.type === "primary" ? "bg-brand-ink text-white" : "bg-brand-gold text-white"}`}>{b.type === "primary" ? "Principal" : "Secundário"}</span>
                                    <span className="text-xs text-brand-inkSoft">Ordem {b.order}</span>
                                </div>
                                <h3 className="font-heading font-semibold text-brand-ink">{b.title || "(sem título)"}</h3>
                                <p className="text-sm text-brand-inkSoft line-clamp-2">{b.subtitle}</p>
                                {b.button_text && <div className="mt-2 text-xs text-brand-gold font-semibold">→ {b.button_text} ({b.link_type}: {b.link_value || "—"})</div>}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => toggle(b)} className={`text-[10px] font-bold uppercase px-3 py-1.5 rounded-sm ${b.is_active ? "bg-brand-success/10 text-brand-success" : "bg-brand-bg text-brand-inkSoft"}`}>{b.is_active ? "Ativo" : "Inativo"}</button>
                                <button onClick={() => { setEditing(b); setOpen(true); }} data-testid={`banner-edit-${b.id}`} className="w-9 h-9 hover:bg-brand-bg rounded-sm flex items-center justify-center"><Edit2 className="w-4 h-4" /></button>
                                <button onClick={() => remove(b)} data-testid={`banner-delete-${b.id}`} className="w-9 h-9 hover:bg-red-50 text-brand-error rounded-sm flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="text-center py-12 text-brand-inkSoft text-sm">Nenhum banner. Clique em "Novo banner".</div>}
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar banner" : "Novo banner"}>
                <BannerForm initial={editing || {}} products={products} categories={categories} onSubmit={save} onCancel={() => setOpen(false)} />
            </Modal>
        </div>
    );
};

export default Banners;
